/* G Decor — Predictive search with smart category redirects
   - Opens panel on click
   - Debounced predictive search via Shopify predictive_search route (locale-aware)
   - Renders suggestions + product results
   - Category-name queries (e.g. 'glassware', 'candles') redirect to collection pages
   May 2026
*/
(function () {
  'use strict';

  // Map common category-name queries to their collection URLs.
  // Keys are lowercase, normalized (no hyphens, single spaces).
  // When the user's query EXACTLY matches one of these, the search redirects
  // to the collection page instead of returning mixed search results.
  var CATEGORY_REDIRECTS = {
    'hardware': '/collections/hardware',
    'tableware': '/collections/kitchen-dining',
    'kitchen and dining': '/collections/kitchen-dining',
    'glassware': '/collections/glassware',
    'glasses': '/collections/glassware',
    'candles': '/collections/candles',
    'candle': '/collections/candles',
    'candle holders': '/collections/candle-holders',
    'home decor': '/collections/home-decor',
    'home décor': '/collections/home-decor',
    'decor': '/collections/home-decor',
    'décor': '/collections/home-decor',
    'gifts': '/collections/gifts',
    'gift': '/collections/gifts',
    'new in': '/collections/new-in',
    'new': '/collections/new-in',
    'new arrivals': '/collections/new-in',
    'best sellers': '/collections/best-sellers',
    'best seller': '/collections/best-sellers',
    'bestsellers': '/collections/best-sellers',
    'brassworks': '/collections/the-brassworks-edit',
    'the brassworks edit': '/collections/the-brassworks-edit',
    'brassworks edit': '/collections/the-brassworks-edit',
    'hand painted': '/collections/the-hand-painted-edit',
    'hand-painted': '/collections/the-hand-painted-edit',
    'handpainted': '/collections/the-hand-painted-edit',
    'the hand-painted edit': '/collections/the-hand-painted-edit',
    'cabinet knobs': '/collections/cabinet-knobs',
    'cabinet hardware': '/collections/hardware',
    'door knobs': '/collections/internal-door-knobs',
    'door hardware': '/collections/door-fittings',
    'mugs': '/collections/mugs-cups',
    'mugs and cups': '/collections/mugs-cups',
    'dinnerware': '/collections/dinnerware',
    'wine glasses': '/collections/wine-glasses',
    'cocktail glasses': '/collections/cocktail-glasses',
    'tumblers': '/collections/tumblers',
    'wall hooks': '/collections/hooks-all-wall-hooks',
    'hooks': '/collections/hooks-all-wall-hooks',
    'monogram coasters': '/collections/monogram-coasters',
    'pillar candles': '/collections/pillar-candles',
    'dinner candles': '/collections/dinner-candles',
    'scented candles': '/collections/scented-candles',
    'bathroom accessories': '/collections/bathroom-accessories',
    'photo frames': '/collections/photo-frames',
    'planters': '/collections/planters-vases',
    'vases': '/collections/planters-vases',
    'planters and vases': '/collections/planters-vases',
    'lighting': '/collections/lighting',
    'stories': '/blogs/stories',
    'journal': '/blogs/stories',
    'the journal': '/blogs/stories',
    'the edits': '/pages/the-edits',
    'edits': '/pages/the-edits',
    'bespoke': '/pages/bespoke-commissions',
    'trade': '/pages/trade-programme',
    'trade programme': '/pages/trade-programme',
    'care': '/pages/care-restoration',
    'care and restoration': '/pages/care-restoration'
  };

  // Locale-aware: theme.routes.predictiveSearch already includes the locale prefix.
  var PREDICTIVE_ROUTE = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
  var SUGGEST_URL = PREDICTIVE_ROUTE.replace(/\.json$/, '') + '.json';

  function getSearchUrl() {
    var form = document.querySelector('[data-gd-search-panel] form');
    if (form && form.getAttribute('action')) return form.getAttribute('action');
    return '/search';
  }

  // Detect locale prefix from current URL (e.g. '/en-us' on US, empty on UK)
  function getLocalePrefix() {
    var path = window.location.pathname;
    var m = path.match(/^(\/[a-z]{2}-[a-z]{2})(?:\/|$)/);
    return m ? m[1] : '';
  }

  // Normalize a query for category lookup: lowercase, collapse whitespace, replace hyphens
  function normalize(term) {
    return String(term || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  // If term exactly matches a category, return the locale-aware URL. Otherwise null.
  function categoryUrlFor(term) {
    var key = normalize(term);
    var rel = CATEGORY_REDIRECTS[key];
    if (!rel) return null;
    return getLocalePrefix() + rel;
  }

  // Pretty-print a category term for display ("glassware" → "Glassware")
  function prettyCategory(term) {
    return String(term || '')
      .toLowerCase()
      .split(/\s+/)
      .map(function (w) {
        if (!w) return '';
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(' ');
  }

  var MONEY_FORMAT = (window.theme && window.theme.settings && window.theme.settings.moneyWithCurrencyFormat) || '£{{amount}} GBP';

  var panel, input, results, emptyState, liveState, form;
  var debounceTimer = null;
  var activeRequest = null;
  var lastQuery = '';

  function init() {
    panel = document.querySelector('[data-gd-search-panel]');
    if (!panel) return;

    input      = panel.querySelector('[data-gd-search-input]');
    results    = panel.querySelector('[data-gd-search-results]');
    emptyState = panel.querySelector('[data-gd-search-empty]');
    liveState  = panel.querySelector('[data-gd-search-live]');
    form       = panel.querySelector('form');

    var openers = document.querySelectorAll('[data-gd-search-open]');
    openers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    var closers = panel.querySelectorAll('[data-gd-search-close]');
    closers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    if (input) {
      input.addEventListener('input', onInput);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.hasAttribute('data-gd-search-open')) close();
    });

    // Intercept form submission: if category match, redirect to collection page
    if (form) {
      form.addEventListener('submit', function (e) {
        var term = input.value.trim();
        if (!term) {
          e.preventDefault();
          return;
        }
        var catUrl = categoryUrlFor(term);
        if (catUrl) {
          e.preventDefault();
          window.location.href = catUrl;
        }
        // Otherwise let the form submit naturally to /search?q=...
      });
    }
  }

  function open() {
    panel.setAttribute('data-gd-search-open', '');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gd-search-locked');
    showEmpty();
    setTimeout(function () {
      if (input) input.focus();
    }, 120);
  }

  function close() {
    panel.removeAttribute('data-gd-search-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gd-search-locked');
    if (input) input.value = '';
    showEmpty();
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (activeRequest) {
      activeRequest.abort();
      activeRequest = null;
    }
    lastQuery = '';
  }

  function onInput(e) {
    clearTimeout(debounceTimer);
    var term = e.target.value.trim();
    if (!term) {
      showEmpty();
      lastQuery = '';
      return;
    }
    if (term === lastQuery) return;
    debounceTimer = setTimeout(function () { fetchResults(term); }, 220);
  }

  function showEmpty() {
    if (emptyState) emptyState.style.display = '';
    if (liveState)  liveState.style.display  = 'none';
  }
  function showLive() {
    if (emptyState) emptyState.style.display = 'none';
    if (liveState)  liveState.style.display  = '';
  }

  function showLoading() {
    showLive();
    liveState.innerHTML = '<div class="gd-search-results__loading" aria-live="polite" aria-label="Searching">' +
      '<span class="gd-search-results__loading-dot"></span>' +
      '<span class="gd-search-results__loading-dot"></span>' +
      '<span class="gd-search-results__loading-dot"></span>' +
      '</div>';
  }

  function fetchResults(term) {
    if (activeRequest) activeRequest.abort();
    activeRequest = new AbortController();
    lastQuery = term;
    showLoading();

    var params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product,query');
    params.set('resources[limit]', '6');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,tag');

    fetch(SUGGEST_URL + '?' + params.toString(), { signal: activeRequest.signal })
      .then(function (r) { return r.json(); })
      .then(function (data) { render(data, term); })
      .catch(function (err) {
        if (err.name !== 'AbortError') {
          console.error('GD search:', err);
          renderError(term);
        }
      });
  }

  function render(data, term) {
    var queries  = (data && data.resources && data.resources.results && data.resources.results.queries)  || [];
    var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
    var catUrl   = categoryUrlFor(term);

    if (queries.length === 0 && products.length === 0 && !catUrl) {
      renderEmpty(term);
      return;
    }

    var html = '';
    var searchUrl = getSearchUrl();

    // Category match — surface as the top suggestion
    if (catUrl) {
      html += '<div class="gd-search-results__section">';
      html += '<p class="gd-search-results__eyebrow">Browse the collection</p>';
      html += '<a href="' + escapeAttr(catUrl) + '" class="gd-search-category-link">';
      html += '<svg class="gd-search-category-link__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 7l9-4 9 4v10l-9 4-9-4V7zM3 7l9 4 9-4M12 11v10" stroke-linejoin="round"/></svg>';
      html += '<span class="gd-search-category-link__text">View the <em>' + escapeHTML(prettyCategory(term)) + '</em> collection</span>';
      html += '<svg class="gd-search-category-link__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 12h14m-6-6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      html += '</a>';
      html += '</div>';
    }

    // Queries (suggested completions)
    if (queries.length > 0) {
      html += '<div class="gd-search-results__section">';
      html += '<p class="gd-search-results__eyebrow">Suggestions</p>';
      html += '<ul class="gd-search-queries__list">';
      queries.slice(0, 3).forEach(function (q) {
        var text = q.text || '';
        var url  = q.url  || (searchUrl + '?q=' + encodeURIComponent(text));
        html += '<li class="gd-search-queries__item">';
        html += '<a href="' + escapeAttr(url) + '" class="gd-search-queries__link">';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5" stroke-linecap="round"/></svg>';
        html += highlightTerm(text, term);
        html += '</a></li>';
      });
      html += '</ul></div>';
    }

    // Products
    if (products.length > 0) {
      html += '<div class="gd-search-results__section">';
      html += '<p class="gd-search-results__eyebrow">Pieces</p>';
      html += '<div class="gd-search-products__list">';
      products.slice(0, 6).forEach(function (p) {
        var title = p.title || '';
        var url   = p.url   || '#';
        var image = '';
        if (p.featured_image && p.featured_image.url) image = p.featured_image.url;
        else if (typeof p.featured_image === 'string') image = p.featured_image;
        else if (p.image) image = p.image;
        var price = formatMoney(p.price);
        html += '<a href="' + escapeAttr(url) + '" class="gd-search-product">';
        html += '<div class="gd-search-product__image">';
        if (image) {
          html += '<img src="' + escapeAttr(resizeImage(image, 400)) + '" alt="' + escapeAttr(title) + '" loading="lazy" decoding="async">';
        }
        html += '</div>';
        html += '<p class="gd-search-product__name">' + escapeHTML(title) + '</p>';
        html += '<p class="gd-search-product__price">' + price + '</p>';
        html += '</a>';
      });
      html += '</div>';
      // If category match, don't show "View all results" — we already gave them the collection link.
      // Otherwise, link to full search results page.
      if (!catUrl) {
        html += '<a href="' + escapeAttr(searchUrl + '?q=' + encodeURIComponent(term)) + '" class="gd-search-viewall">View all results &rarr;</a>';
      }
      html += '</div>';
    }

    showLive();
    liveState.innerHTML = html;
  }

  function renderEmpty(term) {
    showLive();
    liveState.innerHTML =
      '<div class="gd-search-results__empty">' +
      '<p class="gd-search-results__empty-title">No pieces found for “' + escapeHTML(term) + '”.</p>' +
      '<p class="gd-search-results__empty-text">Try a different word, or explore our categories.</p>' +
      '</div>';
  }

  function renderError(term) {
    showLive();
    liveState.innerHTML =
      '<div class="gd-search-results__empty">' +
      '<p class="gd-search-results__empty-title">Couldn’t reach search.</p>' +
      '<p class="gd-search-results__empty-text">Please try again, or press Enter to view the full results page.</p>' +
      '</div>';
  }

  // === Helpers ===

  function highlightTerm(text, term) {
    if (!term) return escapeHTML(text);
    var re = new RegExp('(' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
    return escapeHTML(text).replace(re, '<mark>$1</mark>');
  }

  function escapeHTML(s) {
    s = String(s == null ? '' : s);
    return s.replace(/[&<>"']/g, function (m) {
      return ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[m];
    });
  }

  function escapeAttr(s) { return escapeHTML(s); }

  function resizeImage(url, width) {
    if (!url) return '';
    try {
      var base = window.location.origin;
      var u = new URL(url, base);
      if (u.hostname.indexOf('cdn.shopify.com') === -1 && u.pathname.indexOf('/cdn/shop/') === -1) {
        return url;
      }
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function formatMoney(price) {
    if (price == null) return '';
    var cents = typeof price === 'string' ? parseInt(price, 10) : price;
    if (isNaN(cents)) return '';
    var amount = cents / 100;
    try {
      var currency = 'GBP';
      var m = MONEY_FORMAT.match(/[A-Z]{3}/);
      if (m) currency = m[0];
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return '£' + amount.toFixed(2);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
